import type {
  ContentBlockT,
  ConversationMessageT,
  FileAttachmentContentBlockBodyT,
  FileAttachmentTypeT,
} from '@lotushealth/lotushealth_js_sdk/schemas';
import { User } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { FileAttachmentContentBlock } from '@/entities/file-attachments';
import lotusMorphUrl from '@/shared/assets/images/LotusMorph.png';
import { formatTimeLabel } from '@/shared/lib';
import { ContentBlocks } from '@/shared/ui/primitives';
import { cn } from '@/lib/utils';

const LOTUS_AI_NAME = 'Lotus AI';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

function createFileAttachmentRenderer(userId: string) {
  return function renderFileAttachmentBlock(block: ContentBlockT): ReactNode {
    const body = block.body;
    if (!isRecord(body)) return null;

    const attachmentId = body.attachmentId;
    if (typeof attachmentId !== 'string' || attachmentId.trim() === '') return null;

    const filenameValue = body.filename;
    const filename =
      filenameValue === null || typeof filenameValue === 'string' ? filenameValue : undefined;

    const mimeTypeValue = body.mimeType;
    const mimeType =
      mimeTypeValue === null || typeof mimeTypeValue === 'string' ? mimeTypeValue : undefined;

    const fileTypeValue = body.fileType;
    const fileType: FileAttachmentTypeT | null | undefined =
      fileTypeValue === null || fileTypeValue === 'image' || fileTypeValue === 'document'
        ? fileTypeValue
        : undefined;

    const safeBody: FileAttachmentContentBlockBodyT = {
      type: 'fileAttachment',
      attachmentId,
      filename,
      mimeType,
      fileType,
    };

    return <FileAttachmentContentBlock userId={userId} body={safeBody} />;
  };
}

export interface ChatMessageRowProps {
  userId: string;
  message: ConversationMessageT;
  patientName: string;
  highlighted?: boolean;
}

export function ChatMessageRow({ userId, message, patientName, highlighted }: ChatMessageRowProps) {
  const isUser = message.sender === 'User';

  const renderersByType = useMemo(
    () => ({
      fileAttachment: createFileAttachmentRenderer(userId),
    }),
    [userId]
  );

  const timeLabel = formatTimeLabel(message.createdAt);

  if (isUser) {
    return (
      <div className="flex w-full flex-col items-end gap-1 px-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">{patientName}</span>
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary">
            <User className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          </div>
        </div>
        <div
          className={cn(
            'max-w-[80%] rounded-[22px] bg-secondary px-4 py-2.5 text-sm text-foreground',
            highlighted && 'ring-2 ring-primary/40 ring-offset-2'
          )}
        >
          <ContentBlocks blocks={message.contentBlocks} renderersByType={renderersByType} />
        </div>
        <span className="text-[11px] text-muted-foreground">{timeLabel}</span>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-start gap-1 px-2">
      <div className="flex items-center gap-2">
        <img src={lotusMorphUrl} alt="" aria-hidden="true" className="h-7 w-7 shrink-0" />
        <span className="text-xs font-medium text-muted-foreground">{LOTUS_AI_NAME}</span>
      </div>
      <div
        className={cn(
          'w-full rounded-[12px] text-sm text-foreground',
          highlighted && 'ring-2 ring-primary/40 ring-offset-2'
        )}
      >
        <ContentBlocks blocks={message.contentBlocks} renderersByType={renderersByType} />
      </div>
      <span className="text-[11px] text-muted-foreground">{timeLabel}</span>
    </div>
  );
}
